import { useCallback, useMemo } from 'react';

// Third party
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingButton from '@mui/lab/LoadingButton';
import { useMediaQuery, useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

// Components
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import {
  BaseDrawer,
  BaseDrawerTitle,
  BaseLabel,
} from '@mgm/common/components/base';
import { BASE_COLORS } from '@mgm/common/utils';
import {
  BaseSelect,
} from '../../../../components/base';
import {
  NumericField,
} from '../../../../components/form';
// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useCommonAPI } from '../../../../hooks/api';
import { useTransferVariantQuantity } from '../../../../hooks/api/useInventory';
import { useTransferVariantLocation } from '../../hooks/useTransferVariantLocation';

// Redux-toolkit
import { getPlatformData } from '../../../../store/common/selectors';

// Types
import {
  TTransferQuantity,
} from '../../../../types';
import { TTransferVariant } from '../../../../types/responses/inventory.type';

// Data
import { getIsTransferWindowOpen, getSelectedVariant } from '../../../../store/inventory/selector';
import { inventoryActions } from '../../../../store/inventory/slice';
import { commonActions } from '../../../../store/common/slice';

const TransferVariant = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { loadLocations } = useCommonAPI();
  const { mutate: transferQuantity, isLoading: isUpdateVariantLoading } = useTransferVariantQuantity();
  const selectedVariant = useAppSelector(getSelectedVariant);

  const {
    setFormInputs,
    formInputs,
  } = useTransferVariantLocation();

  const dispatch = useAppDispatch();

  const { t } = useTranslation(['inputs', 'common', 'welcome', 'team', 'settings', 'commissions']);
  const platformData = useAppSelector(getPlatformData);
  const isTransferWindowOpen = useAppSelector(getIsTransferWindowOpen);

  const methods = useForm<Exclude<TTransferQuantity, 'password' | 'fullName'>>({
    mode: 'all',
    values: {
      selectetVariant: formInputs.selectetVariant,
      targetLocation: formInputs.targetLocation,
      quantity: formInputs.quantity,
      selectedQuantityType: formInputs.selectedQuantityType,
    },
  });

  const dataByMode = useMemo(() => ({
    title: 'Transfer variants',
    mainBtnText: t('Transfer quantity', { ns: 'team' }),
  }), ['a']);

  const handleFormChange = useCallback((key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  }, []);

  const transferVariant = () => {
    const [type, countString] = formInputs?.selectedQuantityType ? formInputs.selectedQuantityType.toString().split(' ') : ['', ''];
    const availableCount = Number(countString?.replace(/[()]/g, ''));

    if (formInputs.quantity > availableCount) {
      console.error(`Quantity (${formInputs.quantity}) cannot be greater than available count (${availableCount}).`);
      return;
    }

    dispatch(inventoryActions.setSelectedVariant({
      selectetVariant: selectedVariant?.selectetVariant,
      quantity: formInputs.quantity,
      selectedQuantityType: type,
      targetLocation: formInputs.targetLocation,
    } as TTransferQuantity));
    const transferData = {
      sourceLocationId: Number(selectedVariant?.selectetVariant?.node.inventoryItem.inventoryLevel?.location.id.toString().match(/\/(\d+)$/)?.[1]),
      targetLocationId: platformData.locations.find((l) => l.name === formInputs.targetLocation)?.id,
      inventoryItemId: selectedVariant?.selectetVariant?.node.inventoryItem.id,
      quantityType: type,
      quantity: Number(formInputs.quantity),
    };

    transferQuantity(transferData as TTransferVariant);
  };

  const handleCloseNav = () => {
    dispatch(inventoryActions.setIsTransferWindowOpen(false));

    setTimeout(() => {
      dispatch(inventoryActions.setSelectedVariant({} as TTransferQuantity));
    }, 300);
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 650,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isTransferWindowOpen}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={methods.handleSubmit(transferVariant)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <div className="justify-space-between">
                <div className="align-center">
                  <BaseLabel letterSpacing="-0.5px">{t('target location')}</BaseLabel>
                  <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
                </div>
                <FontAwesomeIcon
                  onClick={() => loadLocations.mutate()}
                  color={theme.palette.primary.main}
                  spin={loadLocations.isLoading}
                  className="cursor-pointer"
                  icon={['fas', 'rotate']}
                />
              </div>

              <BaseSelect
                selectProps={{
                  items: platformData.locations.filter((l) => l.id > 1 && l.name !== formInputs.selectetVariant?.node.inventoryItem.inventoryLevel?.location.name).map((l) => l.name),
                  variant: 'outlined',
                  onChange: (e) => handleFormChange('targetLocation', e.target.value),
                }}
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <div className="justify-space-between">
                <div className="align-center">
                  <BaseLabel letterSpacing="-0.5px">{t('quantity type')}</BaseLabel>
                  <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
                </div>
                <FontAwesomeIcon
                  onClick={() => loadLocations.mutate()}
                  color={theme.palette.primary.main}
                  spin={loadLocations.isLoading}
                  className="cursor-pointer"
                  icon={['fas', 'rotate']}
                />
              </div>
              <BaseSelect
                selectProps={{
                  items: formInputs.selectetVariant?.node.inventoryItem.inventoryLevel?.quantities.map((t) => `${t.name} (${t.quantity})`) || [],
                  variant: 'outlined',
                  onChange: (e) => handleFormChange('selectedQuantityType', e.target.value),
                }}
              />
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('quantity')}</BaseLabel>
              <NumericField
                borderRadius={1}
                onChange={(quantity) => handleFormChange('quantity', quantity)}
                name="quantity"
              />
            </Grid>

            <AppBar
              position="fixed"
              color="default"
              sx={{
                top: 'auto',
                bottom: 0,
                width: isMobile ? '100%' : 650,
              }}
            >
              <Toolbar sx={{
                width: 400,
              }}
              >
                <LoadingButton
                  aria-label="transfer"
                  loading={isUpdateVariantLoading}
                  type="submit"
                  size="large"
                  variant="contained"
                >
                  {dataByMode.mainBtnText}
                </LoadingButton>
              </Toolbar>
            </AppBar>
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  TransferVariant,
};
